
import { useState, useCallback } from 'react'
import { useToasts } from 'react-toast-notifications'
import { endPointCaderno } from '../../models/constantes'

/**
 * Hook de serviço para recursos - combina lógicas de formulário e API
 * Integra as funcionalidades do RecursoService com gerenciamento de estado
 */
const useRecursoService = ({initialData = null, useWebInterface, wsProvider, useToastService}) => {
  // Estado do formulário
  const [formData, setFormData] = useState({
    nome: initialData?.nome || '',
    descricao: initialData?.descricao || '',
    active: initialData?.active !== undefined ? initialData.active : true,
    agrupamento: initialData?.agrupamento || 'default'
  })

  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [agrupamentos, setAgrupamentos] = useState([])
  const { addToast } = useToastService()

  // Cliente HTTP para comunicação com a API
  const httpClient = useWebInterface({baseEndPoint : endPointCaderno, context : "recurso", wsProvider: wsProvider?.wsEvent})

  // =================== MÉTODOS DE API (vindos do RecursoService) ===================

  const create = useCallback(async (recursoData) => {
    if (!httpClient) throw new Error('WebInterface não está disponível')
    const response = await httpClient.post('/recurso', recursoData)
    return response
  }, [httpClient])

  const update = useCallback(async (id, recursoData) => {
    if (!httpClient) throw new Error('WebInterface não está disponível')
    const response = await httpClient.put(`/recurso/${id}`, recursoData)
    return response
  }, [httpClient])

  const getById = useCallback(async (id) => {
    if (!httpClient) throw new Error('WebInterface não está disponível')
    const response = await httpClient.get(`/recurso/${id}`)
    return response
  }, [httpClient])

  const loadAgrupamentos = useCallback(async () => {
    if (!httpClient) {
      console.warn('WebInterface não está disponível para carregar agrupamentos')
      return []
    }
    
    try {
      setIsLoading(true)
      const response = await httpClient.get(`/listarAgrupamentos`)
      const agrupamentosList = response || []
      setAgrupamentos(agrupamentosList)
      return agrupamentosList
    } catch (error) {
      console.error('Erro ao carregar agrupamentos:', error)
      addToast('Erro ao carregar agrupamentos', { appearance: 'error' })
      return []
    } finally {
      setIsLoading(false)
    }
  }, [httpClient, addToast])

  const validateRecurso = useCallback(async (recursoData) => {
    console.log('Validating recursoData:', recursoData)
    const errors = {}
    
    if (!recursoData.nome || recursoData.nome.trim() === '') {
      errors.nome = 'Nome é obrigatório'
    }
    
    if (!recursoData.descricao || recursoData.descricao.trim() === '') {
      errors.descricao = 'Descrição é obrigatória'
    }
    
    if (recursoData.nome && recursoData.nome.length > 255) {
      errors.nome = 'Nome deve ter no máximo 255 caracteres'
    }
    
    if (recursoData.descricao && recursoData.descricao.length > 500) {
      errors.descricao = 'Descrição deve ter no máximo 500 caracteres'
    }

    console.log('Validation errors:', errors)
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }, [])

  // =================== MÉTODOS DE FORMULÁRIO ===================

  const updateField = useCallback((field, value) => {
    console.log('updateField called:', { field, value })
    setFormData(prev => {
      console.log('Previous formData:', prev)
      const newFormData = {
        ...prev,
        [field]: value
      }
      console.log('New formData:', newFormData)
      return newFormData
    })
    setIsDirty(true)
    
    // Limpar erro do campo quando o usuário começar a digitar
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev }
        delete newErrors[field]
        console.log('Cleared error for field:', field)
        return newErrors
      }
      return prev
    })
  }, [])

  const validate = useCallback(async () => {
    const validation = await validateRecurso(formData)
    setErrors(validation.errors)
    return validation.isValid
  }, [formData, validateRecurso])

  const reset = useCallback(() => {
    setFormData({
      nome: initialData?.nome || '',
      descricao: initialData?.descricao || '',
      active: initialData?.active !== undefined ? initialData.active : true,
      agrupamento: initialData?.agrupamento || 'default'
    })
    setErrors({})
    setIsDirty(false)
  }, [initialData])

  const submit = useCallback(async (onSuccess) => {
    setIsLoading(true)
    
    try {
      const isValid = await validate()
      
      if (!isValid) {
        addToast('Por favor, corrija os erros antes de continuar', { 
          appearance: 'error' 
        })
        return false
      }

      let result
      if (initialData?.id) {
        result = await update(initialData.id, formData)
        addToast('Recurso atualizado com sucesso!', { 
          appearance: 'success' 
        })
      } else {
        result = await create(formData)
        addToast('Recurso criado com sucesso!', { 
          appearance: 'success' 
        })
      }

      if (onSuccess) {
        onSuccess(result)
      }
      
      setIsDirty(false)
      return true
    } catch (error) {
      console.error('Erro ao salvar recurso:', error)
      addToast('Erro ao salvar recurso. Tente novamente.', { 
        appearance: 'error' 
      })
      return false
    } finally {
      setIsLoading(false)
    }
  }, [formData, initialData, create, update, validate, addToast])

  return {
    // Estado do formulário
    formData,
    errors,
    isLoading,
    isDirty,
    agrupamentos,
    
    // Métodos de formulário
    updateField,
    validate,
    reset,
    submit,
    
    // Métodos de API
    create,
    update,
    getById,
    loadAgrupamentos,
    validateRecurso
  }
}

export default useRecursoService